import { createQuestionTemplate, getTemplatesByTradeName } from '../../services/QuestionBuilderService';
import { addTrade, getTradeDetails, getTrades, updateTrade } from '../../services/TradeConfigurationServics';
import { serializeToSdmDocument } from '../utils/sdm-serializer';
import type { SDM } from '../types/sdm';

export type SaveType = 'draft' | 'publish';

export interface SaveResult {
  ok: boolean;
  tradeOid?: string;
  message?: string;
}

export interface LoadResult {
  ok: boolean;
  data?: string;
  status?: string;
  tradeOid?: string;
  tradeLogo?: string;
  message?: string;
}

export async function saveNaomiTemplate(
  sdm: SDM,
  type: SaveType,
  authorName: string,
  accessToken: string,
): Promise<SaveResult> {
  const tradeName = sdm.GlobalVariables.tradeName || sdm.GlobalVariables.Name;
  const tradeCategory = sdm.GlobalVariables.tradeCategory || sdm.GlobalVariables.Name || tradeName;

  let tradeOid = (sdm.GlobalVariables as any).tradeOid || '';
  if (!tradeOid) {
    const tradeRes = await getTradeDetails(tradeName, accessToken);
    if (tradeRes.ok && tradeRes.data?.tradeOid) {
      return {
        ok: false,
        message: `A trade named "${tradeName}" already exists. Please rename your template before saving.`,
      };
    }
    const addRes = await addTrade(
      {
        name: tradeName,
        type: tradeCategory,
        code: deriveTradeCode(),
        description: sdm.GlobalVariables.Description || '',
      },
      accessToken,
    );
    if (!addRes?.ok) {
      throw new Error(addRes?.message || `Failed to create trade "${tradeName}"`);
    }
    const created = await getTradeDetails(tradeName, accessToken);
    if (!created.ok || !created.data?.tradeOid) {
      throw new Error(`Trade created but OID lookup failed for "${tradeName}": ${created.message ?? 'unknown error'}`);
    }
    tradeOid = created.data.tradeOid;
  } else {
    const renameRes = await syncTradeNameIfRenamed(sdm.GlobalVariables.Name, tradeOid, accessToken);
    if (!renameRes.ok) {
      return { ok: false, message: renameRes.message };
    }
  }

  if (!tradeOid) {
    return { ok: false, message: `Could not resolve trade OID for "${tradeName}". Save aborted.` };
  }

  const [firstName, ...rest] = authorName.split(' ');
  const lastName = rest.join(' ') || 'User';
  const gmsSdm = serializeToSdmDocument(sdm, firstName || 'GMS', lastName, tradeOid, type === 'draft');

  const dto = {
    tradeOid,
    name: sdm.GlobalVariables.Name,
    createdDate: new Date().toISOString(),
    createdBy: authorName,
    status: 'active',
    invalid: false,
    questionTemplateData: JSON.stringify(gmsSdm),   // stored as JSON string, not object
    type: tradeCategory,
  };

  const res = await createQuestionTemplate(dto, type, accessToken);
  if (!res.ok) {
    return { ok: false, message: res.message || `${type} failed` };
  }
  return { ok: true, tradeOid };
}

const ASSET_OID = 'A.1';

export async function saveNaomiAssetTemplate(
  sdm: SDM,
  type: SaveType,
  authorName: string,
  accessToken: string,
): Promise<SaveResult> {
  const [firstName, ...rest] = authorName.split(' ');
  const lastName = rest.join(' ') || 'User';
  const gmsSdm = serializeToSdmDocument(sdm, firstName || 'GMS', lastName, ASSET_OID, type === 'draft');

  const dto = {
    tradeOid: ASSET_OID,
    name: 'asset',
    createdDate: new Date().toISOString(),
    createdBy: authorName,
    status: 'active',
    invalid: false,
    questionTemplateData: JSON.stringify(gmsSdm),
    type: 'asset',
  };

  const res = await createQuestionTemplate(dto, type, accessToken);
  if (!res.ok) {
    return { ok: false, message: res.message || `${type} failed` };
  }
  return { ok: true };
}

export async function loadNaomiTemplate(
  tradeName: string,
  accessToken: string,
): Promise<LoadResult> {
  try {
    const tradeRes = await getTradeDetails(tradeName, accessToken);
    if (!tradeRes.ok || !tradeRes.data?.tradeOid) {
      return { ok: false, message: 'Trade not found' };
    }
    const tradeOid: string = tradeRes.data.tradeOid;
    const tplRes = await getTemplatesByTradeName(tradeOid, accessToken);
    if (!tplRes.ok || !tplRes.data?.questionTemplateData) {
      return { ok: false, message: 'No template found for this trade' };
    }
    return {
      ok: true,
      data: tplRes.data.questionTemplateData,
      status: tplRes.data.status,
      tradeOid,
      tradeLogo: tradeRes.data.tradeLogo ?? '',
    };
  } catch (err: any) {
    return { ok: false, message: err?.message ?? 'Load failed' };
  }
}

export async function loadNaomiTemplateByTradeOid(
  tradeOid: string,
  accessToken: string,
): Promise<LoadResult> {
  try {
    const tplRes = await getTemplatesByTradeName(tradeOid, accessToken);
    if (!tplRes.ok || !tplRes.data?.questionTemplateData) {
      return { ok: false, message: 'No template found' };
    }
    return { ok: true, data: tplRes.data.questionTemplateData, status: tplRes.data.status, tradeOid };
  } catch (err: any) {
    return { ok: false, message: err?.message ?? 'Load failed' };
  }
}

function deriveTradeCode(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase();
}


function normalizeTradeName(name: string): string {
  return (name || '').toLowerCase().replace(/\s/g, '');
}

async function syncTradeNameIfRenamed(
  newName: string,
  tradeOid: string,
  accessToken: string,
): Promise<{ ok: boolean; message?: string }> {
  const tradesRes = await getTrades(accessToken);
  if (!tradesRes?.ok || !Array.isArray(tradesRes.data)) {
    return { ok: false, message: 'Unable to verify the trade name. Please try again.' };
  }
  const trade = tradesRes.data.find((t: any) => t.tradeOid === tradeOid);
  if (!trade || trade.name === newName) {
    return { ok: true };
  }
  const duplicate = tradesRes.data.some(
    (t: any) => t.tradeOid !== tradeOid && normalizeTradeName(t.name) === normalizeTradeName(newName),
  );
  if (duplicate) {
    return {
      ok: false,
      message: `A trade named "${newName}" already exists. Please rename your template before saving.`,
    };
  }
  const renamedTrade = { ...trade, name: newName };
  if (!trade.type || trade.type === trade.name) {
    renamedTrade.type = newName;
  }
  const res = await updateTrade(trade.id, renamedTrade, accessToken);
  if (!res?.ok || res.data !== true) {
    return { ok: false, message: res?.message || `Failed to rename trade to "${newName}". Save aborted.` };
  }
  return { ok: true };
}
